import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useAuth } from '@shared/auth/AuthContext';
import { FAQ, type FaqItem } from './faq';

/**
 * Floating help launcher ("?") + support panel: a searchable FAQ and a "write us"
 * form with screenshot attachments (→ submit_feedback / feedback-uploads bucket).
 * Contacts live in the footer / landing, NOT here. Mounted once per logged-in shell.
 */
const CATEGORIES = [
  { v: 'question', d: 'Вопрос' }, { v: 'problem', d: 'Проблема' }, { v: 'bug', d: 'Ошибка / баг' },
  { v: 'review', d: 'Отзыв' }, { v: 'idea', d: 'Предложение' },
];
const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;

/** Opens the support panel from anywhere (e.g. a header button when the floating «?» is off). */
export const openSupport = () => window.dispatchEvent(new Event('adamar:open-support'));

export function SupportWidget({ bottomOffset = '18px', onReplayTour, onDeleteAccount, fab = true }: { bottomOffset?: string; onReplayTour?: () => void; onDeleteAccount?: () => void; fab?: boolean }) {
  const { t } = useTranslation('common');
  const tr = (k: string, d: string) => t(k, { defaultValue: d }) as string;
  const { user, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'faq' | 'write'>('faq');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const [category, setCategory] = useState('question');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // FAQ relevant to the caller's role.
  const items = useMemo(() => {
    const aud = role === 'creator' ? 'creator' : role === 'customer' ? 'business' : null;
    const base = aud ? FAQ.filter((f) => f.audience === aud || f.audience === 'both') : FAQ;
    const q = query.trim().toLowerCase();
    return q ? base.filter((f) => (f.q + ' ' + f.a).toLowerCase().includes(q)) : base;
  }, [role, query]);

  const pickFiles = (list: FileList | null) => {
    if (!list) return;
    setErr(null);
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= MAX_FILES) { setErr(tr('support.maxFiles', 'Не более 3 изображений.')); break; }
      if (!f.type.startsWith('image/')) { setErr(tr('support.imgOnly', 'Только изображения.')); continue; }
      if (f.size > MAX_BYTES) { setErr(tr('support.tooBig', 'Каждый файл до 5 МБ.')); continue; }
      next.push(f);
    }
    setFiles(next);
  };

  const submit = async () => {
    if (message.trim().length < 3 || !user) return;
    setErr(null); setSending(true);
    try {
      const paths: string[] = [];
      for (const [i, f] of files.entries()) {
        const ext = (f.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
        const path = `${user.id}/${Date.now()}_${i}.${ext}`;
        const { error } = await supabase.storage.from('feedback-uploads').upload(path, f, { upsert: false });
        if (error) throw error;
        paths.push(path);
      }
      const { error } = await supabase.rpc('submit_feedback', { p_category: category, p_message: message.trim(), p_images: paths });
      if (error) throw error;
      setSent(true); setMessage(''); setFiles([]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener('adamar:open-support', on);
    return () => window.removeEventListener('adamar:open-support', on);
  }, []);

  const close = () => { setOpen(false); setTimeout(() => { setSent(false); setErr(null); setTab('faq'); setQuery(''); setExpanded(null); }, 200); };

  return (
    <>
      {fab && <button type="button" aria-label={tr('support.open', 'Помощь')} onClick={() => setOpen(true)}
        style={{ position: 'fixed', right: 18, bottom: `calc(${bottomOffset} + env(safe-area-inset-bottom))`, zIndex: 60,
          width: 46, height: 46, borderRadius: '50%', cursor: 'pointer', border: 'none', color: '#fff', fontSize: 22, fontWeight: 700,
          fontFamily: 'inherit', background: 'var(--primary)', boxShadow: '0 8px 24px rgba(0,0,0,0.45)' }}>?</button>}

      {open && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 61, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto',
            background: 'var(--bg-elev, #12151c)', border: '1px solid var(--border-strong, rgba(255,255,255,0.12))',
            borderRadius: '18px 18px 0 0', padding: '18px 18px calc(22px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 17 }}>{tr('support.title', 'Помощь и поддержка')}</h3>
              <button type="button" onClick={close} aria-label="close" style={{ background: 'none', border: 0, color: 'var(--muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setTab('faq')} style={pill(tab === 'faq')}>{tr('support.tabFaq', 'Частые вопросы')}</button>
              <button type="button" onClick={() => setTab('write')} style={pill(tab === 'write')}>{tr('support.tabWrite', 'Написать нам')}</button>
            </div>

            {tab === 'faq' ? (
              <>
                {onReplayTour && (
                  <button type="button" onClick={() => { close(); onReplayTour(); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '11px 13px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500, color: '#bcd9ff', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.35)' }}>
                    <span>{tr('support.replayTour', 'Показать вводный тур')}</span>
                    <span aria-hidden="true" style={{ opacity: 0.7 }}>→</span>
                  </button>
                )}
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tr('support.search', 'Поиск по вопросам…')}
                  style={inp} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {items.map((f, i) => <FaqRow key={i} item={f} open={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)} />)}
                  {items.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 2px' }}>{tr('support.noMatch', 'Ничего не найдено.')}</div>}
                </div>
                <button type="button" onClick={() => setTab('write')} style={{ ...pill(false), alignSelf: 'flex-start', marginTop: 4 }}>
                  {tr('support.notFound', 'Не нашли ответ? Напишите нам →')}
                </button>
                {onDeleteAccount && (
                  <button type="button" onClick={() => { close(); onDeleteAccount(); }}
                    style={{ alignSelf: 'center', marginTop: 10, background: 'none', border: 0, color: '#a87f7f', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
                    {tr('support.deleteAccount', 'Удалить аккаунт')}
                  </button>
                )}
              </>
            ) : sent ? (
              <div style={{ padding: 14, borderRadius: 10, background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.4)', fontSize: 14 }}>
                {tr('support.thanks', 'Спасибо! Мы получили ваше сообщение и ответим на почту.')}
              </div>
            ) : (
              <>
                <select value={category} onChange={(e) => setCategory(e.target.value)} style={inp}>
                  {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{tr(`support.cat.${c.v}`, c.d)}</option>)}
                </select>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                  placeholder={tr('support.placeholder', 'Опишите вопрос…')} style={{ ...inp, resize: 'vertical' }} />

                {/* Screenshot attachments */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {files.map((f, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px' }}>
                      {f.name.length > 18 ? f.name.slice(0, 16) + '…' : f.name}
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ background: 'none', border: 0, color: 'var(--muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                  {files.length < MAX_FILES && (
                    <label style={{ fontSize: 12.5, color: 'var(--accent, #4a9eff)', cursor: 'pointer' }}>
                      + {tr('support.attach', 'Скриншот')}
                      <input type="file" accept="image/*" multiple onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
                    </label>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--dim)' }}>{tr('support.attachHint', 'до 3, ≤5 МБ')}</span>
                </div>

                {err && <div style={{ color: 'var(--danger,#e8607a)', fontSize: 13 }}>{err}</div>}
                <button type="button" onClick={submit} disabled={sending || message.trim().length < 3}
                  style={{ padding: '11px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#fff',
                    opacity: sending || message.trim().length < 3 ? 0.6 : 1, background: 'var(--primary)' }}>
                  {sending ? '…' : tr('support.send', 'Отправить')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function FaqRow({ item, open, onToggle }: { item: FaqItem; open: boolean; onToggle: () => void }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.02)' }}>
      <button type="button" onClick={onToggle} style={{ width: '100%', textAlign: 'left', padding: '11px 13px', background: 'none', border: 0, color: 'var(--text,#eee)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <span>{item.q}</span><span style={{ color: 'var(--muted)', flexShrink: 0 }}>{open ? '−' : '+'}</span>
      </button>
      {open && <div style={{ padding: '0 13px 13px', fontSize: 13, lineHeight: 1.55, color: 'var(--muted)' }}>{item.a}</div>}
    </div>
  );
}

function pill(active: boolean): React.CSSProperties {
  return { fontSize: 12.5, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
    border: active ? '1px solid transparent' : '1px solid var(--border-strong, rgba(255,255,255,0.14))',
    background: active ? 'rgba(74,158,255,0.16)' : 'transparent', color: active ? '#bcd9ff' : 'var(--muted)' };
}
const inp: React.CSSProperties = { padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: 'var(--text,#eee)', border: '1px solid var(--border)', fontFamily: 'inherit', fontSize: 14, width: '100%', boxSizing: 'border-box' };
